import React from 'react';
import { theme as T, PAGE } from './theme';
import { txt, list, rowsOf, extrasOf } from './model';
import { DOCUMENT_DISCLAIMER, DOCUMENT_BRAND, US_FOOTER } from '../documentBranding';

export const SEP = ' \u00A0·\u00A0 ';
export const BAR = ' \u00A0|\u00A0 ';

export const L = {
  project: 'PROJECT', type: 'TYPE', catalogNumber: 'CATALOG NUMBER', specifier: 'SPECIFIER', date: 'DATE', memo: 'MEMO',
  specifications: 'SPECIFICATIONS', dimensions: 'DIMENSIONAL DETAILS', photometrics: 'PHOTOMETRICS',
  notes: 'NOTES', orderingExample: 'Ordering example:', rev: 'REV', page: 'PAGE', of: 'OF',
};

// One fixed Letter page (816 × 1056 px). Flex column; <PageFooter> last pins to the bottom.
export function Page({ id, label, children }) {
  return (
    <section className="ds-page usds-kit" id={id} data-screen-label={label} data-testid={'us-page-' + id}
      style={{ display: 'flex', flexDirection: 'column', width: PAGE.width, height: PAGE.height, boxSizing: 'border-box', padding: PAGE.padding, background: T.white, color: T.ink, fontFamily: T.font, overflow: 'hidden', flex: '0 0 auto', position: 'relative' }}>
      {children}
    </section>
  );
}

// html2canvas does not honour object-fit: use background-size:contain for every raster.
export function Img({ src, w = '100%', h, alt, style, pos = 'center' }) {
  if (!txt(src)) return null;
  return <div role="img" aria-label={alt || ''} style={{ width: w, height: h, backgroundImage: `url(${src})`, backgroundSize: 'contain', backgroundPosition: pos, backgroundRepeat: 'no-repeat', flex: '0 0 auto', ...style }} />;
}

export function LogoStack({ logos = {}, width = 210, primaryHeight = 108, secondaryHeight = 36, gap = 8, style }) {
  return (
    <div data-testid="us-brand-lockup" style={{ display: 'flex', alignItems: 'center', gap, flex: '0 0 auto', width, minHeight: Math.min(primaryHeight, 64), ...style }}>
      <Img src={logos.primary} w={width * .34} h={Math.min(primaryHeight, 60)} alt="LPA Lighting" />
      <div style={{ width: 1, alignSelf: 'stretch', background: T.rule, minHeight: 30, flexShrink: 0 }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
        {[logos.secondary, logos.tertiary].filter(Boolean).map((src, i, all) => <Img key={i} src={src} w={`calc((100% - ${(all.length - 1) * 6}px) / ${all.length})`} h={Math.min(primaryHeight, 48)} alt={'Additional brand ' + (i + 1)} />)}
      </div>
    </div>
  );
}

export function CoverHeader({ title, kicker, logos }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 28 }}>
      <div style={{ flex: '1 1 auto', minWidth: 0 }}>
        <h1 style={{ margin: 0, fontSize: 32, lineHeight: 1.08, fontWeight: 900, letterSpacing: 0, color: T.inkDark, textTransform: 'uppercase', minHeight: 35 }}>{title}</h1>
        {txt(kicker) && <div style={{ marginTop: 8, fontSize: 11, fontWeight: 800, letterSpacing: 0, color: T.navy }}>{kicker}</div>}
      </div>
      <LogoStack logos={logos} style={{ marginTop: 4 }} />
    </div>
  );
}

export function PageHeader({ title, subtitle, mode, logos }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', borderBottom: `3px solid ${T.navy}`, paddingBottom: 8, gap: 20 }}>
      <div style={{ flex: '1 1 auto', minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 900, letterSpacing: 0.5, color: T.inkDark, textTransform: 'uppercase', minHeight: 16 }}>{title}</div>
        {txt(subtitle) && <div style={{ fontSize: 7.5, fontWeight: 800, letterSpacing: 2, color: T.navy, marginTop: 2 }}>{subtitle}</div>}
      </div>
      {mode !== 'catalogue' && (
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', fontSize: 7.5, fontWeight: 800, letterSpacing: 1, color: T.navy }}>
          <div>{L.project} <span style={{ display: 'inline-block', width: 80, borderBottom: `1px solid ${T.rule}` }} /></div>
          <div>{L.type} <span style={{ display: 'inline-block', width: 60, borderBottom: `1px solid ${T.rule}` }} /></div>
        </div>
      )}
      <LogoStack logos={logos} width={130} primaryHeight={62} secondaryHeight={22} gap={4} style={{ marginBottom: -2 }} />
    </div>
  );
}

export function PageFooter({ meta, label, page, total }) {
  const c = { ...meta.company, ...US_FOOTER };
  const linkStyle = { color: T.muted, textDecoration: 'none' };
  const right = [txt(label).toUpperCase(), txt(meta.revision) && `${L.rev} ${meta.revision}`, txt(meta.year) && `© ${meta.year} ${txt(c.name)}`].filter(Boolean).join(' · ');
  const testid = 'us-footer-' + label.replace(/[^a-z0-9]/gi, '-').toLowerCase() + '-' + page;
  return (
    <div data-testid={testid} data-page={page} data-total={total} style={{ marginTop: 'auto', flexShrink: 0, paddingTop: 8, borderTop: `3px solid ${T.navy}` }}>
      <div data-testid={testid + '-disclaimer'} style={{ fontSize: 8.5, color: T.muted, lineHeight: 1.4, marginBottom: 8 }}>{DOCUMENT_DISCLAIMER}</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16 }}>
        <div>
          <div data-testid={testid + '-brand'} style={{ fontSize: 11, fontWeight: 900, letterSpacing: 0, color: T.navy }}>{DOCUMENT_BRAND}</div>
          <div data-testid={testid + '-contact'} style={{ fontSize: 7.5, color: T.muted, marginTop: 2 }}>
            {txt(c.site) && <a href={c.url || `https://${c.site}`} target="_blank" rel="noreferrer" data-testid={testid + '-website'} style={linkStyle}>{c.site}</a>}
            {txt(c.email) && <>{txt(c.site) ? SEP : ''}<a href={`mailto:${c.email}`} data-testid={testid + '-email'} style={linkStyle}>{c.email}</a></>}
            {txt(c.tagline) && <>{SEP}{txt(c.tagline)}</>}
          </div>
        </div>
        <div data-testid={testid + '-pagination'} style={{ textAlign: 'right', fontSize: 7.5, color: T.muted, lineHeight: 1.5 }}>
          {meta.documentStatus && <div style={{ fontWeight: 800, color: T.navy }}>{meta.documentStatus}</div>}{right}{right && <br />}
          <span style={{ fontWeight: 800, color: T.navy, letterSpacing: 1 }}>{L.page} {page} {L.of} {total}</span>
        </div>
      </div>
    </div>
  );
}

export const SectionTitle = ({ children, top = 20 }) => <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: 2.5, color: T.navy, borderBottom: `3px solid ${T.navy}`, paddingBottom: 6, margin: `${top}px 0 0`, textTransform: 'uppercase' }}>{children}</div>;
export const DataTitle = ({ children }) => <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 2.2, color: T.navy, borderBottom: `2.5px solid ${T.navy}`, paddingBottom: 5, margin: '0 0 8px', textTransform: 'uppercase' }}>{children}</div>;
export const Kicker = ({ children, style }) => <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.8, color: T.navy, textTransform: 'uppercase', ...style }}>{children}</div>;
export const Note = ({ children, style }) => (txt(children) ? <div style={{ fontSize: 8.5, color: T.muted, ...style }}>{children}</div> : null);

export function Bullets({ items, size = 9.5, lineHeight = 1.6, pad = 14, style }) {
  const it = list(items);
  if (!it.length) return null;
  return <ul style={{ margin: 0, paddingLeft: pad, fontSize: size, lineHeight, color: T.ink, ...style }}>{it.map((t, i) => <li key={i}>{t}</li>)}</ul>;
}

export function Chips({ items, style, itemStyle }) {
  const it = list(items);
  if (!it.length) return null;
  return <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap', ...style }}>{it.map((t, i) => <div key={i} style={{ background: T.navy, color: T.white, fontSize: 9, fontWeight: 800, letterSpacing: 1.5, padding: '6px 14px', textTransform: 'uppercase', ...itemStyle }}>{t}</div>)}</div>;
}

export function Band({ items, style }) {
  const it = list(items);
  if (!it.length) return null;
  return <div style={{ marginTop: 16, background: T.navy, color: T.white, textAlign: 'center', fontSize: 10, fontWeight: 800, letterSpacing: 1.6, padding: '8px 10px', textTransform: 'uppercase', ...style }}>{it.join(BAR)}</div>;
}

export function FieldBox({ label, value, tinted = false, style }) {
  return (
    <div style={{ border: `1.5px solid ${T.navy}`, padding: '6px 10px 8px', background: tinted ? T.tint : 'transparent', ...style }}>
      <div style={{ fontSize: 7.5, fontWeight: 800, letterSpacing: 1.5, color: T.navy }}>{label}</div>
      {txt(value) ? <div style={{ fontSize: 12.5, fontWeight: 800, marginTop: 3, color: T.inkDark, overflowWrap: 'anywhere' }}>{value}</div> : <div style={{ height: 16 }} />}
    </div>
  );
}

const GRID3 = { display: 'grid', gridTemplateColumns: '1.1fr 0.7fr 1.2fr', gap: 10 };

export function HeaderFields({ mode, catalogNumber, specifierRow, show = true }) {
  if (!show) return null;
  if (mode === 'catalogue') {
    return txt(catalogNumber) ? <div style={{ display: 'flex', marginTop: 12 }}><FieldBox label={L.catalogNumber} value={catalogNumber} tinted style={{ minWidth: 260 }} /></div> : null;
  }
  return (
    <>
      <div style={{ ...GRID3, marginTop: 12 }}><FieldBox label={L.project} /><FieldBox label={L.type} /><FieldBox label={L.catalogNumber} value={catalogNumber} tinted /></div>
      {specifierRow && <div style={{ ...GRID3, marginTop: 10 }}><FieldBox label={L.specifier} /><FieldBox label={L.date} /><FieldBox label={L.memo} /></div>}
    </>
  );
}

export function DataList({ rows, padY = 6.5 }) {
  const rs = rowsOf(rows);
  if (!rs.length) return null;
  return (
    <div>
      {rs.map((r, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: `${padY}px 10px`, background: i % 2 === 0 ? T.tint : 'transparent' }}>
          <span style={{ fontSize: 8.5, fontWeight: 800, letterSpacing: 1, color: T.navy, textTransform: 'uppercase' }}>{r.label}</span>
          <span style={{ fontSize: 10, textAlign: 'right' }}>{r.value}</span>
        </div>
      ))}
    </div>
  );
}

export function TitledRows({ data, padY }) {
  if (!data || !rowsOf(data.rows).length) return null;
  return <div>{txt(data.title) && <DataTitle>{data.title}</DataTitle>}<DataList rows={data.rows} padY={padY} /></div>;
}

export function InfoBox({ box, style }) {
  if (!box) return null;
  const it = list(box.items);
  if (!it.length && !txt(box.note)) return null;
  return (
    <div style={{ border: `1.5px solid ${T.navy}`, padding: '9px 12px', ...style }}>
      {txt(box.title) && <div style={{ fontSize: 8.5, fontWeight: 800, letterSpacing: 1.5, color: T.navy, textTransform: 'uppercase' }}>{box.title}</div>}
      {it.length > 0 && <div style={{ marginTop: 5, fontSize: 10 }}>{it.join(SEP)}</div>}
      <Note style={{ marginTop: 4 }}>{box.note}</Note>
    </div>
  );
}

export function Figure({ fig, height, captionStyle }) {
  if (!fig || (!txt(fig.src) && !fig.pending)) return null;
  return (
    <div>
      {txt(fig.src) ? <Img src={fig.src} h={height} alt={fig.alt} /> : <div data-testid={'us-artwork-pending-' + (fig.key || 'hero')} style={{ height, border: `1px dashed ${T.rule}`, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18, boxSizing: 'border-box', fontSize: 10, textAlign: 'center', color: T.muted }}>{fig.placeholder || 'Factory artwork pending'}</div>}
      {txt(fig.caption) && <div style={{ textAlign: 'center', fontSize: 10, fontWeight: 800, letterSpacing: 1.5, color: T.navy, marginTop: 8, textTransform: 'uppercase', ...captionStyle }}>{fig.caption}</div>}
      {txt(fig.subcaption) && <div style={{ textAlign: 'center', fontSize: 8.5, color: T.muted, marginTop: 3 }}>{fig.subcaption}</div>}
    </div>
  );
}

export function Badge({ top, bottom, variant = 'label' }) {
  const warranty = variant === 'warranty';
  const bottomStyle = variant === 'value' ? { fontSize: 8.5 } : { fontSize: 5.5, fontWeight: warranty ? 800 : 700, letterSpacing: 0.5 };
  return (
    <div style={{ width: 42, height: 42, borderRadius: '50%', background: warranty ? T.green : T.navy, color: T.white, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', fontWeight: warranty ? 900 : 800, fontSize: warranty ? 15 : 9, lineHeight: warranty ? 1 : 1.1 }}>
      <span>{top}</span><span style={bottomStyle}>{bottom}</span>
    </div>
  );
}

export function BadgeRow({ badges, style }) {
  const bs = (badges || []).filter(b => b && (txt(b.top) || txt(b.bottom)));
  if (!bs.length) return null;
  return <div style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', marginTop: 8, paddingTop: 10, borderTop: `1px solid ${T.tintLight}`, ...style }}>{bs.map((b, i) => <Badge key={i} {...b} />)}</div>;
}

export function BulletGroup({ group, top = 0, lineHeight }) {
  if (!group || !list(group.items).length) return null;
  return <div style={{ marginTop: top }}>{txt(group.title) && <Kicker style={{ margin: '0 0 6px' }}>{group.title}</Kicker>}<Bullets items={group.items} lineHeight={lineHeight} /></div>;
}

export function CodeBar({ segments, style }) {
  const sg = (segments || []).filter(s => s && (txt(s.code) || txt(s.label)));
  if (!sg.length) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 5, marginTop: 12, ...style }}>
      {sg.map((s, i) => (
        <React.Fragment key={i}>
          {i > 0 && <div style={{ fontWeight: 800, fontSize: 14, paddingTop: 7, color: T.navy }}>–</div>}
          <div style={{ textAlign: 'center', flex: Number(s.flex) > 0 ? Number(s.flex) : 1 }}>
            <div style={{ border: `1.5px solid ${T.navy}`, background: T.tint, fontWeight: 800, fontSize: 13, padding: '7px 4px', color: T.inkDark, minHeight: 31, boxSizing: 'border-box' }}>{s.code}</div>
            <div style={{ marginTop: 4, fontSize: 6, fontWeight: 800, letterSpacing: 0.8, color: T.navy, lineHeight: 1.3, textTransform: 'uppercase' }}>{s.label}</div>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

export function CodeTable({ table, style }) {
  const cols = (table.columns || []).filter(c => c && (txt(c.title) || (c.options || []).some(o => txt(o.code) || txt(o.label))));
  if (!cols.length) return null;
  const dense = !!table.dense;
  const tpl = txt(table.template) && cols.length === (table.columns || []).length ? table.template : `repeat(${cols.length},1fr)`;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: tpl, gap: 1, background: T.line, border: `1px solid ${T.line}`, ...style }}>
      {cols.map((c, i) => <div key={'h' + i} style={{ background: T.navy, color: T.white, fontWeight: 800, letterSpacing: dense ? 0.6 : 0.8, fontSize: dense ? 7.5 : 8, padding: dense ? '6px 7px' : '6px 8px', textTransform: 'uppercase' }}>{c.title}</div>)}
      {cols.map((c, i) => (
        <div key={'b' + i} style={{ background: T.white, padding: dense ? '8px 7px' : 8, fontSize: dense ? 8 : 8.5, lineHeight: 1.5, columnCount: Number(c.columns) > 1 ? Number(c.columns) : undefined, columnGap: Number(c.columns) > 1 ? 10 : undefined }}>
          {(c.options || []).filter(o => txt(o.code) || txt(o.label)).map((o, j) => <div key={j}><b>{txt(o.code)}</b>{txt(o.code) && txt(o.label) ? ' – ' : ''}{txt(o.label)}</div>)}
        </div>
      ))}
    </div>
  );
}

export function GridTable({ table, tone = 'navy', style }) {
  if (!table) return null;
  const columns = list(table.columns);
  const rows = (table.rows || []).filter(r => (r || []).some(txt));
  if (!rows.length) return null;
  const n = Math.max(columns.length, ...rows.map(r => r.length));
  const head = tone === 'mid' ? { background: T.navyMid, fontWeight: 700, fontSize: 8, letterSpacing: 0.8, padding: '5px 9px' } : { background: T.navy, fontWeight: 800, fontSize: 8, letterSpacing: 0.8, padding: '6px 9px' };
  const tpl = txt(table.template) && (table.template.split(/\s+/).length === n || /repeat\(/.test(table.template)) ? table.template : `repeat(${n},1fr)`;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: tpl, gap: 1, background: T.line, border: `1px solid ${T.line}`, ...style }}>
      {columns.length > 0 && Array.from({ length: n }).map((_, i) => <div key={'h' + i} style={{ color: T.white, textTransform: 'uppercase', ...head }}>{columns[i] || ''}</div>)}
      {rows.map((r, i) => Array.from({ length: n }).map((_, j) => <div key={i + '-' + j} style={{ background: i % 2 ? T.tint : T.white, padding: '7px 9px', fontSize: 8.5 }}>{txt(r[j])}</div>))}
    </div>
  );
}

// User-added custom blocks (rows / bullets / paragraph) — rendered at the end of any page.
export function Extras({ blocks, top = 14 }) {
  const bs = extrasOf(blocks);
  if (!bs.length) return null;
  return (
    <div data-testid="us-extras">
      {bs.map((b, i) => (
        <div key={i} style={{ marginTop: i === 0 ? top : 12 }}>
          {txt(b.title) && <Kicker style={{ margin: '0 0 5px' }}>{b.title}</Kicker>}
          {b.kind === 'rows' && <DataList rows={b.rows} padY={5.5} />}
          {b.kind === 'bullets' && <Bullets items={b.items} size={9} />}
          {b.kind === 'text' && txt(b.text) && <p style={{ margin: 0, fontSize: 9.5, lineHeight: 1.6, color: T.ink, whiteSpace: 'pre-wrap' }}>{b.text}</p>}
        </div>
      ))}
    </div>
  );
}
